import * as React from 'react';
import { Box, Flex } from '../CommonUI/Layout';
import { TrustpilotSpinner, Badge, NotificationAlert, NotificationType } from '../CommonUI';
import Button from '../CommonUI/Control/Button';
import { Text } from 'ilenia';
import { ShopComponent, PeriodComponent, ModalComponent, ProgressBarsComponent, PastOrdersSummary } from '../Components/EnhancedPastOrders';
import { connect } from 'react-redux';
import * as actions from '../Actions/PastOrders/index';
import * as tracking from '../Actions/analytics';
import { Settings, InvitationsSettings, PastOrdersStatus, ConfigurationScope, EmailImageType, TierFeature, EmailSentFrom, EmailType } from '../Model';
import { css, StyleSheet } from 'aphrodite';
import EmailExample from '../Components/Common/EmailExample';
import { Utils } from '../Utils';
import { ProductReviewsToggle } from '../Components';
import { InfoIcon } from '../Components/Common/InfoIcon';

interface EnhancedPastOrdersProps {
    productReviewsEnabled?: boolean;
    invitationSettings: InvitationsSettings;
    syncInProgress: boolean;
    isFetching?: boolean;
    status: PastOrdersStatus;
    pastOrders: any;
    configurationScopeTree: ConfigurationScope[];
    period: string;
    isFetchingPastOrdersSettings?: boolean;
    features: string[];
    settings: Settings;
    tierName: string;
    isProductReviewsEnabled: boolean;
    fetchPastOrdersSettings: () => {};
    setRenderModal: (renderModal: boolean) => {};
    startSync: () => void;
    onProductReviewsSettingChange: (selector: Settings) => void;
    onSettingsSave: () => void;
    fetchStatus: () => void;
    track: (name: string, data: any) => void;
    pageView: () => void;
    sendPostMessage: (message: string | object) => void;
    onLoggedInChange: (toggle: boolean) => void;
    onConsentChange: (toggle: boolean) => void;
    onToggleSettingsSave: (value: boolean) => void;
}

class EnhancedPastOrders extends React.Component<EnhancedPastOrdersProps, any> {
    private timer: NodeJS.Timeout | null = null;

    constructor(props: EnhancedPastOrdersProps) {
        super(props);
        this.state = {
            enableProductReviews: undefined,
        };

        props.pageView();
        props.fetchPastOrdersSettings();
        this.onSync = this.onSync.bind(this);
        this.sendPostMessage = this.sendPostMessage.bind(this);
        this.toggleChanged = this.toggleChanged.bind(this);
    }

    public componentDidMount() {
        const { fetchStatus } = this.props;
        fetchStatus();
        this.timer = setInterval(() => {
            const { status } = this.props;
            if (status.inProgress) {
                fetchStatus();
            }
        }, 30000);
    }

    public componentWillUnmount() {
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }
    }

    public toggleChanged(value: boolean) {
      if (value) {
          NotificationAlert.alert(NotificationType.Success, 'Notifications.productReviewsEnabled');
      }
      this.setState({enableProductReviews: value});
    }

    public renderPastOrderStatus() {
        const { status, invitationSettings, pastOrders } = this.props;

        if (status.inProgress) {
          return <ProgressBarsComponent />;
        } else if (pastOrders.showInitial) {
            return <EmailExample
                invitationSettings={invitationSettings}
                style={styles.emailPreview}
                imageType={EmailImageType.PastOrders}
                sentFrom={EmailSentFrom.PastOrders}
                templateType={EmailType.ServiceReviews}
            />;
        } else {
          return <PastOrdersSummary />;
        }
    }

    public render() {
        const { onProductReviewsSettingChange, onSettingsSave, configurationScopeTree, status, settings, tierName, onLoggedInChange, onConsentChange, onToggleSettingsSave } = this.props;

        const { isFetching, isFetchingPastOrdersSettings } = this.props;
        if ((isFetching && !status.inProgress) || isFetchingPastOrdersSettings) {
            return <TrustpilotSpinner active={true} height="520px" />;
        }

        return (
            <Box mt={0} className={css(styles.layout)}>
                <Flex flexDirection="row">
                    <Box className={css(styles.settingsBox)} mt={0}>
                        <Flex>
                            <Box mt={32} className={css(styles.title)}>
                                <Text id="enhancedPastOrders.title"/>
                            </Box>
                            <Box mt={2} className={css(styles.info)}>
                                <Text id="enhancedPastOrders.note1"/>
                            </Box>
                            <Box className={css(styles.infoBoxContainer)}>
                              <p className={css(styles.iconWrapper)}>
                                <InfoIcon />
                              </p>
                              <p className={css(styles.textWrapper)}>
                                <Text id="enhancedPastOrders.bannerBox"/>
                              </p>
                            </Box>
                            <p className={css(styles.badge)}>
                              <Badge appearance={'success'}>
                                <Text id="enhancedPastOrders.reviewsOn"/>
                              </Badge>
                              { this.state.enableProductReviews ?? this.props.isProductReviewsEnabled ?
                                <Badge appearance={'success'}>
                                  <Text id="enhancedPastOrders.productReviewsOn"/>
                                </Badge>
                              :
                                <Badge appearance={'default'}>
                                  <Text id="enhancedPastOrders.productReviewsOff"/>
                                </Badge>
                              }
                              <ProductReviewsToggle
                                integrationKey={settings.general!.key!}
                                source={'Shopify'}
                                tierName={tierName}
                                onLoggedInChange={onLoggedInChange}
                                onSettingsSave={onSettingsSave}
                                toggleChanged={this.toggleChanged}
                                sku={settings.skuSelector!}
                                onProductReviewsSettingChange={this.props.onProductReviewsSettingChange}
                                productIdentificationOptions={settings.productIdentificationOptions}
                                onConsentChange={onConsentChange}
                                onToggleSettingsSave={onToggleSettingsSave}
                                appearance={'subtle'}
                                mt={1}
                                pl={0}
                              />
                            </p>
                            {
                                configurationScopeTree.length > 1
                                    ? <ShopComponent disabled={status.inProgress} />
                                    : null
                            }
                            <PeriodComponent disabled={status.inProgress} />
                            <ModalComponent
                                onProductReviewsSettingChange={onProductReviewsSettingChange}
                                onSettingsSave={onSettingsSave}
                                sendPostMessage={this.sendPostMessage}
                            />
                            <Button
                              id="syncButtonClick"
                              width={'unset'}
                              onClick={this.onSync}
                              disabled={status.inProgress}
                              pl={0}
                              mt={18}
                            >
                              <Text id="enhancedPastOrders.syncPastOrders"/>
                            </Button>
                        </Flex>
                    </Box>
                    <Box className={css(styles.secondColumn)}>
                    {
                        this.renderPastOrderStatus()
                    }
                    </Box>
                </Flex>
            </Box>
        );
    }

    private onSync() {
        const { productReviewsEnabled, setRenderModal, features } = this.props;

        if (features.indexOf(TierFeature.Reviews_ProductReviews) > -1 && !productReviewsEnabled) {
            setRenderModal(true);
        } else {
            const { track, startSync } = this.props;
            track('Integration Button Clicked', {
                context: 'Ecommscript integration app',
                location: window.location.pathname,
                placement: 'Bottom left corner',
                action: 'Sync past orders clicked',
                name: 'sync-past-orders-button',
            });
            startSync();
            this.sendPostMessage();
        }
    }

    private sendPostMessage() {
        const { period } = this.props;
        const periodInDays = Utils.convertToDays(period);
        if (periodInDays) {
            this.props.sendPostMessage(`sync:${periodInDays}`);
        } else {
            console.log(`Sync process did not started - ${period}`);
        }
    }
}

const mapStateToProps = (state: any) => ({
    productReviewsEnabled: state.PastOrders.productReviewsEnabled,
    invitationSettings: state.PastOrders.invitationSettings,
    syncInProgress: state.PastOrders.syncInProgress,
    status: state.PastOrders.status,
    isFetching: state.PastOrders.isFetching,
    pastOrders: state.PastOrders.pastOrders,
    period: state.PastOrders.period,
    configurationScopeTree: state.App.configurationScopeTree,
    isFetchingPastOrdersSettings: state.PastOrders.isFetchingPastOrdersSettings,
    features: state.PastOrders.features,
    isProductReviewsEnabled: state.PastOrders.productReviewsEnabled
});

const mapDispatchToProps = (dispatch: any) => ({
    fetchPastOrdersSettings: () => dispatch(actions.fetchPastOrdersSettings()),
    setRenderModal: (renderModal: boolean) => dispatch(actions.setRenderModal(renderModal)),
    startSync: () => dispatch(actions.startSync()),
    fetchStatus: () => dispatch(actions.fetchStatus()),
    track: (name: string, data: any) => dispatch(tracking.track(name, data)),
    pageView: () => dispatch(tracking.pageView()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EnhancedPastOrders);

const styles = StyleSheet.create({
    layout: {
      minHeight: '451px'
    },
    settingsBox: {
      maxWidth: '600px',
      minHeight: '451px',
      borderRight: '1px solid rgb(223, 226, 229)',
      paddingRight: '20px',
      paddingBottom: '33px'
    },
    optionalSeting: {
      paddingBottom: '16px',
    },
    greenText: {
      color: 'rgb(0, 182, 122)',
    },
    accordionDescription: {
      padding: '0px 22px',
    },
    title: {
      color: 'rgb(41, 41, 41)',
      fontSize: '20px',
      fontWeight: 'bold',
    },
    info: {
      color: 'rgb(102, 102, 102)',
      fontSize: '14px',
      fontWeight: 'normal',
      letterSpacing: '0px',
      lineHeight: '24px',
      maxWidth: '503px'
    },
    secondColumn: {
      marginTop: 0,
      maxWidth: '600px',
      minHeight: '451px',
      padding: '20px'
    },
    emailPreview: {
      border: 'none',
      background: 'transparent !important',
      maxWidth: '321px',
      display: 'block',
      marginLeft: 'auto',
      marginRight: 'auto',
      width: '53%',
      height: '53%',
      padding: 0
    },
    infoBoxContainer: {
      maxWidth: '503px',
      display: 'flex',
      padding: '12px',
      marginTop: '16px',
      background: '#F3F4F5'
    },
    textWrapper: {
      margin: '0px',
      flex: 1,
      fontSize: '14px',
      lineHeight: '24px',
      letterSpacing: '0.3px'
    },
    iconWrapper: {
      flex: '0 0 auto',
      marginLeft: '2.5px',
      marginTop: '2.5px',
      marginRight: '10.5px',
    },
    badgeContainer: {
      padding: '0 8px',
      borderRadius: '50%',
      display: 'inline-block',
      marginRight: '8px',
      background: 'none',
      color: '#00b67a',
      width: '10px',
      height: '10px'
    },
    badge: {
      marginTop: '24px',
      marginBottom: '14px',
      display: 'inline-flex',
      columnGap: '24px',
      alignItems: 'center',
      justifyContent: 'center',
    },
    select: {
      marginLeft: '0px'
    }
});



// WEBPACK FOOTER //
// ./src/Containers/EnhancedPastOrders.tsx